import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

export default function AeroTable13({ data }) {
  const pdfRef = useRef();

  // ===============================
  // Summary Calculations
  // ===============================
  const totalFemale = data.reduce(
    (sum, item) => sum + parseInt(item.female_2024 || 0, 10),
    0,
  );

  const totalMale = data.reduce(
    (sum, item) => sum + parseInt(item.male_2024 || 0, 10),
    0,
  );

  const highestFemalePercent = data.reduce(
    (max, item) =>
      parseFloat(item.percentage) > max.value
        ? { label: item.age_group_label, value: parseFloat(item.percentage) }
        : max,
    { label: "", value: 0 },
  );

  // ===============================
  // Chart Data (Dual Axis)
  // ===============================
  const chartData = {
    labels: data.map((item) => item.age_group_label),
    datasets: [
      {
        label: "Female Employment (2024)",
        data: data.map((item) => parseInt(item.female_2024, 10)),
        backgroundColor: "#ec4899",
        borderRadius: 6,
        yAxisID: "y",
      },
      {
        label: "Male Employment (2024)",
        data: data.map((item) => parseInt(item.male_2024, 10)),
        backgroundColor: "#0ea5e9",
        borderRadius: 6,
        yAxisID: "y",
      },
      {
        label: "Female Percentage (%)",
        data: data.map((item) => parseFloat(item.percentage)),
        type: "line",
        borderColor: "#10b981",
        backgroundColor: "#10b981",
        tension: 0.3,
        borderWidth: 3,
        yAxisID: "y1",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employment & Female Participation by Age Group (2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        position: "left",
        title: { display: true, text: "Number of Employees" },
      },
      y1: {
        beginAtZero: true,
        position: "right",
        grid: { drawOnChartArea: false },
        title: { display: true, text: "Female Percentage (%)" },
      },
    },
  };

  // ===============================
  // PDF Download
  // ===============================
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.line(10, 35, 200, 35);

      pdf.addImage(imgData, "PNG", 5, 38, imgWidth, imgHeight);

      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("employment_2024_analysis.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment & Female Participation (2024)
        </h2>

        {/* ================= Summary Cards ================= */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Female Employment
            </p>
            <p className="text-xl font-bold text-pink-800">
              {totalFemale.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Male Employment
            </p>
            <p className="text-xl font-bold text-blue-800">
              {totalMale.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Highest Female Participation
            </p>
            <p className="text-xl font-bold text-green-800">
              {highestFemalePercent.label} ({highestFemalePercent.value}%)
            </p>
          </div>
        </div>

        {/* ================= Table + Chart ================= */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* TABLE */}
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="w-full border-collapse text-center">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">Female</th>
                  <th className="p-2">Male</th>
                  <th className="p-2">Female %</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr
                    key={index}
                    className={`hover:bg-gray-100 transition ${
                      row.age_group_label === highestFemalePercent.label
                        ? "bg-green-100 font-bold"
                        : ""
                    }`}
                  >
                    <td className="p-2">{row.age_group_label}</td>
                    <td className="p-2">
                      {parseInt(row.female_2024).toLocaleString("en-US")}
                    </td>
                    <td className="p-2">
                      {parseInt(row.male_2024).toLocaleString("en-US")}
                    </td>
                    <td className="p-2">{row.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CHART */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="min-w-[400px] w-full h-96">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
